import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './api';
import { CreateProspectRequest } from '@clareefai/types';

export interface ProspectResponse {
  id: string;
  vendorId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  company: string | null;
  linkedin?: string | null;
  companySize: string | null;
  sectors: string[];
  assignedPromoters: string[];
  promoterId?: string | null;
  promoterName?: string | null;
  status: string;
  assignedSalespersonId: string | null;
  notes: string | null;
  source: string;
  lastContactDate: string | null;
  expectedValue: number | null;
  actualValue: number | null;
  timeline: Record<string, any> | null;
  metadata: Record<string, any> | null;
  finalizedAt: string | null;
  purchased: boolean | null;
  finalizationFeedback: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface ProspectStatsResponse {
  total: number;
  introduce: number;
  failed: number;
  completed: number;
}

export const prospectApi = createApi({
  reducerPath: 'prospectApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Prospect'],
  endpoints: (builder) => ({
    getProspectStats: builder.query<ProspectStatsResponse, void>({
      query: () => '/prospects/stats',
      providesTags: ['Prospect'],
    }),
    getProspects: builder.query<ProspectResponse[], void>({
      query: () => '/prospects',
      providesTags: ['Prospect'],
    }),
    getProspectById: builder.query<ProspectResponse, string>({
      query: (id) => `/prospects/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Prospect', id }],
    }),
    createProspect: builder.mutation<ProspectResponse, CreateProspectRequest>({
      query: (body) => ({
        url: '/prospects',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Prospect'],
    }),
    updateProspect: builder.mutation<ProspectResponse, { id: string; data: Record<string, any> }>({
      query: ({ id, data }) => ({
        url: `/prospects/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Prospect', id }, 'Prospect'],
    }),
    deleteProspect: builder.mutation<void, string>({
      query: (id) => ({
        url: `/prospects/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Prospect'],
    }),
    resendProspectInvitation: builder.mutation<{ message: string }, string>({
      query: (prospectId) => ({
        url: `/prospects/${prospectId}/resend-invitation`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, prospectId) => [{ type: 'Prospect', id: prospectId }],
    }),
  }),
});

export const {
  useGetProspectStatsQuery,
  useGetProspectsQuery,
  useGetProspectByIdQuery,
  useCreateProspectMutation,
  useUpdateProspectMutation,
  useDeleteProspectMutation,
  useResendProspectInvitationMutation,
} = prospectApi;

